import type { DealerAccessFactsAnswer } from "@hakili/contracts";
import { formatDate, t } from "@hakili/i18n";
import { ERROR_TEXT } from "@hakili/ui";
import { AlertCircle } from "lucide-react";
import { useEffect, useState } from "react";

/**
 * `P1-CNW-064` THE DEALER-ACCESS FACTS PANEL (D-1031-18; the architect's orders of 2 November 2026): **one read-only panel on the console's dossier view**, who at the dealership read
 * the client's identity and situation and which pièces were added. The door is `GET /api/v1/dossiers/{id}/dealer-access-facts`, right `voirEtRelancerDossiers`, newest first
 * (the contract is `packages/contracts/src/dealer-dossier-detail.ts:102-107`, the fact `:41-52`).
 *
 * **The browser composes nothing** (D-1020-7). The fact's `text` is the SERVER's sentence (« Identité et situation consultées par {employé}, le {date} à {heure}. »), drawn verbatim and in the
 * order served: no name, no date, no label is made here, and the list is never sorted. The one thing added is the day in words (`formatDate`) from the served `occurredAt`, and ONLY where the
 * served text does not already carry it. A read the door refused is said in the door's own sentence, never drawn as the empty state; the empty state is only an answer with no fact.
 */
export interface DealerAccessFactsPanelProps {
  readonly dossierId: string;
  readonly read: (dossierId: string) => Promise<DealerAccessFactsAnswer>;
}

/** H's ruled sentence (PROPOSED, catalogue): who sees the facts. It stands under the list and under the empty sentence, never under a refusal. */
function Visibility(): React.JSX.Element {
  return (
    <p data-testid="dossier-acces-visibilite" className="mt-3 text-sm text-gray-500">
      {t("console.dossier.accesVisibilite")}
    </p>
  );
}

const failureOf = (failure: unknown): string =>
  (failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee");

type State =
  | { readonly kind: "loading" }
  | { readonly kind: "read"; readonly answer: DealerAccessFactsAnswer }
  | { readonly kind: "refused"; readonly sentence: string };

export function DealerAccessFactsPanel({ dossierId, read }: DealerAccessFactsPanelProps): React.JSX.Element {
  const [state, setState] = useState<State>({ kind: "loading" });
  useEffect(() => {
    let live = true;
    setState({ kind: "loading" });
    void read(dossierId)
      .then((answer) => {
        if (live) setState({ kind: "read", answer });
      })
      .catch((failure: unknown) => {
        if (live) setState({ kind: "refused", sentence: failureOf(failure) });
      });
    return () => {
      live = false;
    };
  }, [dossierId, read]);

  return (
    <section
      data-testid="dossier-acces-concession"
      className="rounded-2xl border border-gray-200 bg-white px-5 py-4"
    >
      <h2 className="text-sm font-semibold">{t("console.dossier.accesTitre")}</h2>
      <p className="mt-0.5 text-sm text-gray-500">{t("console.dossier.accesSous")}</p>
      {state.kind === "loading" ? (
        <p data-testid="dossier-acces-chargement" className="mt-2 text-xs text-gray-500">
          {t("common.state.chargement")}
        </p>
      ) : state.kind === "refused" ? (
        <p
          role="alert"
          data-testid="dossier-acces-refus"
          className={`mt-2 flex items-start gap-1.5 text-sm ${ERROR_TEXT}`}
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{state.sentence}</span>
        </p>
      ) : state.answer.facts.length === 0 ? (
        <>
          <p data-testid="dossier-acces-vide" className="mt-2 text-sm text-gray-500">
            {t("console.dossier.accesVide")}
          </p>
          <Visibility />
        </>
      ) : (
        <>
          <ul data-testid="dossier-acces-faits" className="mt-2 divide-y divide-gray-100">
            {state.answer.facts.map((fact) => {
              const day = formatDate(new Date(fact.occurredAt));
              return (
                <li key={fact.id} className="py-2">
                  <p data-testid="dossier-acces-texte" className="text-sm text-gray-700">
                    {fact.text}
                  </p>
                  {fact.text.includes(day) ? null : (
                    <p data-testid="dossier-acces-jour" className="text-xs text-gray-500">
                      {day}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
          <Visibility />
        </>
      )}
    </section>
  );
}
